import { useEffect, useState } from 'react';
import { Bell, BellOff, RefreshCw } from 'lucide-react';
import { api } from './api';
import { Loading } from './components';
import { useRemote } from './hooks';
import type { Role, Session, ViewId } from './types';
import { DsrView } from './views/DsrView';
import { ExceptionsView } from './views/ExceptionsView';
import { InsightsView } from './views/InsightsView';
import { KpiView } from './views/KpiView';
import { PerformanceView } from './views/PerformanceView';
import { ScheduleView } from './views/ScheduleView';
import { SourcesView } from './views/SourcesView';
import { AfterSalesView } from './views/AfterSalesView';

type AlertKey = 'kpi' | 'dsr' | 'data' | 'after_sales';
const navigation: Array<{ id?: ViewId; label: string; group?: boolean; nested?: boolean; alert?: AlertKey }> = [
  { id: 'schedule', label: '排班管理' }, { id: 'kpi', label: 'KPI 看板' },
  { label: '售后风险', group: true }, { id: 'afterSales', label: '售后分派', nested: true, alert: 'after_sales' }, { id: 'dsr', label: 'DSR 经营', nested: true, alert: 'dsr' }, { id: 'customerInsights', label: '客户洞察', nested: true },
  { id: 'performance', label: '绩效看板' }, { id: 'sources', label: '数据源监控', alert: 'data' },
];

export function App() {
  const [view, setView] = useState<ViewId>('schedule');
  const session = useRemote<Session>('/session');
  const alerts = useRemote<Record<AlertKey, number>>('/alerts/summary', 30000);
  const [showAlerts, setShowAlerts] = useState(() => window.localStorage.getItem('sidebar-alerts') !== 'off');
  const [message, setMessage] = useState('');
  const [syncing, setSyncing] = useState(false);
  const [login, setLogin] = useState({ username: '', password: '' });
  const [loginError, setLoginError] = useState('');
  const [loggingIn, setLoggingIn] = useState(false);
  useEffect(() => { if (!message) return; const timer = window.setTimeout(() => setMessage(''), 2800); return () => window.clearTimeout(timer); }, [message]);
  const toast = (value: string) => setMessage(value);
  function toggleAlerts() {
    const next = !showAlerts;
    setShowAlerts(next);
    window.localStorage.setItem('sidebar-alerts', next ? 'on' : 'off');
    toast(next ? '左侧预警提示已开启' : '左侧预警提示已关闭');
  }
  async function changeRole(role: Role) {
    try { await api.post('/auth/role-preview', { role }); await session.reload(); toast(`已切换为${role}权限预览`); }
    catch (error) { toast(error instanceof Error ? error.message : '切换失败'); }
  }
  async function sync() {
    setSyncing(true);
    try {
      const result = await api.post<{ dingtalk?: { status: string; message: string } }>('/sync');
      toast(result.dingtalk?.status === 'failed' ? `钉钉读取失败：${result.dingtalk.message}` : '钉钉读取、同步与重新核算已完成');
    }
    catch (error) { toast(error instanceof Error ? error.message : '同步失败'); }
    finally { setSyncing(false); }
  }
  async function signIn(event: React.FormEvent) {
    event.preventDefault(); setLoggingIn(true); setLoginError('');
    try { await api.post('/auth/login', login); await session.reload(); }
    catch (error) { setLoginError(error instanceof Error ? error.message : '登录失败'); }
    finally { setLoggingIn(false); }
  }
  if (session.loading) return <div className="boot"><Loading label="正在进入客服中台…" /></div>;
  if (session.error || !session.data) return <div className="login-page"><form className="login-panel" onSubmit={(event) => void signIn(event)}><div className="brand-mark">服</div><h1>客服中台</h1><p>使用已授权的管理账号登录</p><label><span>用户名</span><input autoComplete="username" value={login.username} onChange={(event) => setLogin({ ...login, username: event.target.value })} /></label><label><span>密码</span><input type="password" autoComplete="current-password" value={login.password} onChange={(event) => setLogin({ ...login, password: event.target.value })} /></label>{loginError && <div className="login-error">{loginError}</div>}<button className="primary" disabled={loggingIn || !login.username || login.password.length < 12}>{loggingIn ? '正在登录…' : '登录'}</button><button type="button" className="text-button" onClick={() => void session.reload()}>重新连接服务</button></form></div>;
  const operationsView = new URLSearchParams(window.location.search).get('view') === 'operations';
  if (!operationsView) {
    return <iframe
      className="workbench-frame"
      src={`/api/v1/workbench/index.html${window.location.hash}`}
      title="客服中台工作台"
    />;
  }
  const role = session.data.actor.role;
  return <div className="app-shell">
    <header className="topbar"><div className="brand"><span className="brand-mark">服</span><strong>客服中台</strong></div><div className="top-actions"><button className="icon-action" onClick={toggleAlerts} aria-label={showAlerts ? '关闭左侧预警提示' : '开启左侧预警提示'} title={showAlerts ? '关闭左侧预警提示' : '开启左侧预警提示'}>{showAlerts ? <Bell size={18} /> : <BellOff size={18} />}</button><button className="primary" onClick={() => void sync()} disabled={syncing}><RefreshCw size={16} className={syncing ? 'spin' : ''} />{syncing ? '正在核算' : '同步并重新核算'}</button></div></header>
    <div className="layout"><aside className="sidebar"><nav>{navigation.map((item, index) => item.group ? <div className="nav-group" key={`${item.label}-${index}`}>{item.label}</div> : <button key={item.id} aria-label={item.label} className={`nav ${item.nested ? 'nested' : ''} ${view === item.id ? 'active' : ''}`} onClick={() => setView(item.id!)}><span>{item.label}</span>{showAlerts && item.alert && Number(alerts.data?.[item.alert] ?? 0) > 0 && <span className="nav-count" aria-hidden="true">{alerts.data?.[item.alert]}</span>}</button>)}</nav><div className="sidebar-note"><b>发布原则</b><p>只有达到应到日期的完整平台进入 KPI；群截图未收到机器人回执时保持“待发送”。</p></div><div className="sidebar-user"><span>客</span><div><b>客服部</b><small>运营中台</small></div></div></aside>
      <main className="main">
        {view === 'schedule' && <ScheduleView role={role} canPreviewRole={session.data.role_preview} onRole={changeRole} toast={toast} />}
        {view === 'kpi' && <KpiView />}
        {view === 'afterSales' && <AfterSalesView toast={toast} />}
        {view === 'dsr' && <DsrView toast={toast} />}
        {view === 'customerInsights' && <InsightsView toast={toast} />}
        {view === 'performance' && <PerformanceView />}
        {view === 'exceptions' && <ExceptionsView toast={toast} />}
        {view === 'sources' && <SourcesView toast={toast} />}
      </main>
    </div>
    <div className={`toast ${message ? 'show' : ''}`} role="status" aria-live="polite">{message}</div>
  </div>;
}
